import React, { useRef, useCallback, useEffect } from "react";
import useCommandFlowStore from "../store/commandFlowStore";
import { wasJustSkipped, markSkip } from "../hooks/terminal/skipLock";

const ZWS = "\u200B";

const placeCursorAtEnd = (el) => {
  if (document.activeElement !== el) return;
  const range = document.createRange();
  const sel = window.getSelection();
  range.selectNodeContents(el);
  range.collapse(false);
  sel.removeAllRanges();
  sel.addRange(range);
};

const TerminalInput = ({
  inputRef,
  onSubmit,
  onKeyDown,
  onFocus,
  onBlur,
  isLoading,
  promptText,
  promptColor,
  inputStyle,
  terminalTextStyle,
}) => {
  const { input, setInput } = useCommandFlowStore();
  const editableRef = useRef(null);
  const formRef = useRef(null);

  // Merge external inputRef with internal editableRef
  const setRefs = useCallback(
    (el) => {
      editableRef.current = el;
      if (inputRef) {
        if (typeof inputRef === "function") inputRef(el);
        else inputRef.current = el;
      }
    },
    [inputRef]
  );

  const handleInput = (e) => {
    const el = e.currentTarget;
    const clean = (el.textContent || "").replace(/\u200B/g, "");
    setInput(clean);
    // Re-add ZWS if user deleted everything (including the ZWS)
    if (!(el.textContent || "").includes(ZWS)) {
      el.textContent = ZWS + clean;
      if (clean) placeCursorAtEnd(el);
    }
  };

  // Force plain-text paste (no HTML)
  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
  };

  const handleKeyDown = (e) => {
    if (onKeyDown) onKeyDown(e);
    if (e.key === "Enter") {
      e.preventDefault();
      if (e.repeat) return;
      if (wasJustSkipped()) {
        // Lock is active from the first action — swallow without refreshing,
        // so the window is bounded by LOCK_MS from the first press.
        return;
      }
      markSkip();
      formRef.current?.requestSubmit();
    }
  };

  // Sync store → contentEditable (history navigation, clearing after submit)
  // Always prefix with ZWS so the span never truly empties (prevents caret issues)
  useEffect(() => {
    if (!editableRef.current) return;
    const current = (editableRef.current.textContent || "").replace(/\u200B/g, "");
    if (current !== input) {
      editableRef.current.textContent = ZWS + input;
      if (input) placeCursorAtEnd(editableRef.current);
    }
  }, [input]);

  // Auto-focus on mount and when loading completes
  // Seed with ZWS so Safari/Chrome render the caret immediately
  useEffect(() => {
    if (editableRef.current && !isLoading) {
      if (!editableRef.current.textContent) {
        editableRef.current.textContent = ZWS;
      }
      editableRef.current.focus();
    }
  }, [isLoading]);

  return (
    <form
      ref={formRef}
      onSubmit={onSubmit}
      autoComplete="off"
      style={{
        marginTop: "0px",
        background: "transparent",
        borderRadius: "6px",
        transition: "background 0.2s",
      }}
    >
      <div
        style={{
          wordWrap: "break-word",
          overflowWrap: "break-word",
          whiteSpace: "pre-wrap",
          ...terminalTextStyle,
        }}
      >
        <span
          style={{
            whiteSpace: "pre-wrap",
            color: promptColor,
            ...terminalTextStyle,
          }}
        >
          {promptText}{" "}
        </span>
        <span
          ref={setRefs}
          contentEditable={!isLoading}
          suppressContentEditableWarning
          onInput={handleInput}
          onPaste={handlePaste}
          onKeyDown={handleKeyDown}
          onFocus={onFocus}
          onBlur={onBlur}
          style={{
            outline: "none",
            padding: 0,
            margin: 0,
            border: "none",
            wordBreak: "break-word",
            caretColor: inputStyle?.caretColor || inputStyle?.color,
            color: inputStyle?.color,
            ...terminalTextStyle,
          }}
        />
      </div>
    </form>
  );
};

export default TerminalInput;
